import React, { useCallback, useEffect, useRef, useState } from "react";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";

import { searchWhatsAppContacts, startWhatsAppConversation } from "../../whatsappApi";
import "./whatsapp.css";

/**
 * ContactList: the "new message" half of the inbox's left pane.
 *
 * The inbox can only ever show conversations a patient started, which makes it
 * useless for the common case: reception needs to message someone. This is the
 * other direction, and it lives in the SAME pane as the thread list rather than
 * in a modal of its own. One search box at the top of that pane, two things it
 * can be searching, and the list underneath swaps. A modal on top of the inbox
 * modal made "start a conversation" feel like a different screen when it is the
 * same task as picking a thread.
 *
 * Only consented contacts appear, and that is the whole list. This is not a
 * patient search with a consent column. A surface whose only action is "message
 * this person" must not offer people the next click would refuse; showing them
 * greyed out reads as an invitation to go and tick a box somewhere.
 *
 * Two kinds of contact, and the distinction matters rather than being cosmetic:
 * a patient is messaged about their care, an account holder about their
 * account. Their consent records are independent (see whatsappInboxService's
 * assertMaySend), so the badge tells the user which conversation they are
 * actually starting. The two are never conflated: an account holder and a
 * patient may share a number and are still different subjects.
 *
 * Conventions: /CLAUDE.md §1.12 (empty and loading states).
 */
export default function ContactList({
  practiceNumber,
  query,
  onStarted,
  showToast,
}) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(null);

  // The request in flight. A slow response for "jo" must not land after the
  // fast one for "john" and repopulate the list with the wrong people.
  const latest = useRef(0);

  const load = useCallback(
    async (term) => {
      if (!practiceNumber) return;
      const ticket = ++latest.current;
      setLoading(true);
      try {
        const data = await searchWhatsAppContacts(practiceNumber, term, 25);
        if (ticket !== latest.current) return;
        setContacts(data.contacts || []);
      } catch (err) {
        if (ticket !== latest.current) return;
        showToast?.(
          "danger",
          err?.response?.data?.error || "Could not load contacts.",
        );
        setContacts([]);
      } finally {
        if (ticket === latest.current) setLoading(false);
      }
    },
    [practiceNumber, showToast],
  );

  // Debounced, and it runs once on mount with an empty term. For a small
  // practice the full consented list is the whole answer and no typing is
  // needed.
  useEffect(() => {
    const t = setTimeout(() => load(query), 250);
    return () => clearTimeout(t);
  }, [query, load]);

  const start = async (contact) => {
    setStarting(`${contact.kind}:${contact.id}`);
    try {
      const { conversation } = await startWhatsAppConversation(practiceNumber, {
        kind: contact.kind,
        id: contact.id,
      });
      onStarted?.(conversation);
    } catch (err) {
      showToast?.(
        "danger",
        err?.response?.data?.error || "Could not start this conversation.",
      );
    } finally {
      setStarting(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading contacts…</p>
      </div>
    );
  }

  if (!contacts.length) {
    return (
      <div className="text-center py-4 px-3">
        <p className="mb-1">No matching contacts.</p>
        <small className="text-muted">
          Only patients and account holders who have consented to WhatsApp and
          have a mobile number appear here.
        </small>
      </div>
    );
  }

  return (
    <div className="wa-contact-list">
      {contacts.map((c) => {
        const key = `${c.kind}:${c.id}`;
        return (
          <button
            type="button"
            key={key}
            className="wa-contact-row"
            disabled={!!starting}
            onClick={() => start(c)}
          >
            <span className="wa-contact-main">
              <span className="fw-semibold">{c.name || "Unnamed"}</span>
              <span className="small text-muted">{c.cellNumber}</span>
            </span>

            <span className="d-flex align-items-center gap-2">
              <Badge bg={c.kind === "patient" ? "primary" : "secondary"}>
                {c.subtitle}
              </Badge>
              {starting === key ? (
                <Spinner size="sm" animation="border" />
              ) : (
                <i className="fa-solid fa-chevron-right text-primary" />
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
